/** Exact addition and comparison of decimal strings - the one place the front end does it.
 *
 * `lib/format.ts` says rule 5 in one sentence: a quantity is a string and is rounded for display
 * only. That leaves one thing a screen legitimately needs arithmetic for: **checking that parts
 * add up to a whole** (ADR-0102, rule 5). `Number` addition cannot do it - `0.1 + 0.2` is not `0.3`
 * - so a "reconciles" claim built on it is true or false by accident of the binary expansion.
 *
 * Scaled `BigInt`, so a sum is exact however many decimals the server sent. A value that is not a
 * plain decimal makes the whole result `undefined`: a screen that cannot add a figure up must say
 * it cannot, never add the rest and call it a total.
 */

const DECIMAL = /^(-?)(\d+)(?:\.(\d+))?$/;

interface Scaled {
  digits: bigint;
  scale: number;
}

function scaled(value: string): Scaled | undefined {
  const match = DECIMAL.exec(value.trim());
  if (!match) return undefined;
  const [, sign, whole, fraction = ""] = match;
  const digits = BigInt(`${whole}${fraction}`);
  return { digits: sign === "-" ? -digits : digits, scale: fraction.length };
}

function written({ digits, scale }: Scaled): string {
  const negative = digits < 0n;
  const text = (negative ? -digits : digits).toString().padStart(scale + 1, "0");
  const whole = scale === 0 ? text : text.slice(0, -scale);
  const fraction = scale === 0 ? "" : `.${text.slice(-scale)}`;
  return `${negative ? "-" : ""}${whole}${fraction}`;
}

function combine(values: readonly string[], sign: bigint | readonly bigint[]): Scaled | undefined {
  const parsed: Scaled[] = [];
  for (const value of values) {
    const one = scaled(value);
    if (!one) return undefined;
    parsed.push(one);
  }
  const scale = Math.max(0, ...parsed.map((p) => p.scale));
  const signs = Array.isArray(sign) ? sign : parsed.map(() => sign);
  const digits = parsed.reduce(
    (sum, p, index) => sum + (signs[index] ?? 1n) * p.digits * 10n ** BigInt(scale - p.scale),
    0n,
  );
  return { digits, scale };
}

/** The exact sum, as a plain decimal string, or `undefined` if any value is not a decimal. */
export function sumDecimals(values: readonly string[]): string | undefined {
  const total = combine(values, 1n);
  return total ? written(total) : undefined;
}

/** `a - b`, exactly, or `undefined` if either is not a decimal. */
export function differenceOf(a: string, b: string): string | undefined {
  const total = combine([a, b], [1n, -1n]);
  return total ? written(total) : undefined;
}

/** Whether two decimal strings are the same amount - `"10.0"` and `"10"` are. `false` if either is
 * not a decimal: unreadable is not equal. */
export function sameAmount(a: string, b: string): boolean {
  const delta = combine([a, b], [1n, -1n]);
  return delta !== undefined && delta.digits === 0n;
}

/** **The one rounding in the product's presentation layer** (S1-02, rule 5).
 *
 * A decimal string rounded to `places` decimal places, **half away from zero** - the rule
 * `regimes/render.py` already applies to a filed figure (`ROUND_HALF_UP` on a `Decimal`), so the
 * filing page and every screen agree on the tie. Exact: the value is scaled `BigInt`, never a
 * `Number`, because `toLocaleString`/`toFixed` round the *binary* expansion (`0.15` is
 * `0.1499999...`) and a tie then goes whichever way the expansion fell.
 *
 * **Rounds once, from the exact string.** The year-on-year bridge used to receive a value the
 * server had already rounded to two places and round it again to one, which is a different answer
 * for about one value in twenty. Nothing may be rounded before it reaches this function; `quantity`
 * and `tonnes` in `format.ts` are its only callers, and a backend test fails if the server rounds.
 *
 * Returns a plain string with no grouping, and **never `-0.0`**: a value that rounds to nothing
 * carries no sign. `undefined` if the input is not a plain decimal - unreadable is not zero.
 */
export function roundDecimal(value: string, places: number): string | undefined {
  const parsed = scaled(value);
  if (!parsed || !Number.isInteger(places) || places < 0) return undefined;
  let { digits } = parsed;
  if (parsed.scale > places) {
    const drop = 10n ** BigInt(parsed.scale - places);
    const negative = digits < 0n;
    const magnitude = negative ? -digits : digits;
    let kept = magnitude / drop;
    if ((magnitude % drop) * 2n >= drop) kept += 1n;
    digits = negative ? -kept : kept;
  } else {
    digits *= 10n ** BigInt(places - parsed.scale);
  }
  return written({ digits, scale: places });
}

/** A decimal string rounded to `figures` **significant** figures, half away from zero, exactly.
 *
 * For a ratio whose size is not known in advance: an intensity of `0.116` and one of `42.37` both
 * need three figures, and a fixed number of decimals is one place too few for the first (it prints
 * `0.1`) and one too many for the second. Never rounds the integer part away (`12345` to three
 * figures is `12345`, not `12300`: a whole number of tonnes is not a ratio to be thinned), and a
 * carry that adds a digit (`0.9996`) is rounded again so the result keeps `figures`, not one more.
 * `undefined` if the input is not a plain decimal or `figures` is not a positive integer.
 */
export function roundSignificant(value: string, figures: number): string | undefined {
  const parsed = scaled(value);
  if (!parsed || !Number.isInteger(figures) || figures < 1) return undefined;
  if (parsed.digits === 0n) return roundDecimal(value, Math.max(0, figures - 1));
  const placesFor = (digits: bigint, scale: number): number => {
    const magnitude = (digits < 0n ? -digits : digits).toString();
    // Digits before the point (negative when the value is below one: 0.0116 has -1).
    const before = magnitude.length - scale;
    return Math.max(0, figures - before);
  };
  const first = roundDecimal(value, placesFor(parsed.digits, parsed.scale));
  if (first === undefined) return undefined;
  const again = scaled(first);
  if (!again) return first;
  const next = placesFor(again.digits, again.scale);
  // Rounding carried into a new leading digit: one fewer place now keeps the figure count.
  return next < again.scale ? roundDecimal(first, next) : first;
}

/** A decimal string divided by 1000, exactly (kilograms to tonnes is a shift of the point). */
export function perThousand(value: string): string | undefined {
  const parsed = scaled(value);
  return parsed ? written({ digits: parsed.digits, scale: parsed.scale + 3 }) : undefined;
}

/** A proportion as a percentage and back, exactly (a shift of the point by two places, never a `Number`):
 * `0.42` is `42`, `0.4250` is `42.50`. `undefined` if the value is not a plain decimal. */
export function timesHundred(value: string): string | undefined {
  const parsed = scaled(value);
  if (!parsed) return undefined;
  return written(
    parsed.scale >= 2
      ? { digits: parsed.digits, scale: parsed.scale - 2 }
      : { digits: parsed.digits * 10n ** BigInt(2 - parsed.scale), scale: 0 },
  );
}

/** A percentage as a proportion: `42` is `0.42`. The inverse of `timesHundred`. */
export function perHundred(value: string): string | undefined {
  const parsed = scaled(value);
  return parsed ? written({ digits: parsed.digits, scale: parsed.scale + 2 }) : undefined;
}

/** How many decimal places a value really has, ignoring the zeros a `NUMERIC` column appended
 * (`"1.0150000000000"` has three). `undefined` if it is not a decimal. */
function significantPlaces(value: string): number | undefined {
  const parsed = scaled(value);
  if (!parsed) return undefined;
  let { digits, scale } = parsed;
  while (scale > 0 && digits % 10n === 0n) {
    digits /= 10n;
    scale -= 1;
  }
  return scale;
}

/** **The footing rule** (S1-02): the fewest decimal places, not below `base`, at which `parts`
 * *as printed* add up to `total` *as printed*.
 *
 * Rounded components do not, in general, sum to the rounded total: `1.015 - 1.005` is `0.010`, and
 * at two places the parts print `1.02` and `1.00` beside a delta of `0.01`. There are three ways
 * out and two are wrong. Largest-remainder allocation makes the parts foot by **moving a figure**
 * to a value it does not have, which on a filed carbon total is the one thing this product must
 * not do. Hiding the difference is the failure this exists to remove. So the rule is the third:
 * **print more decimals**, for that group only, until the printed parts do foot. It always ends,
 * because at the inputs' own precision rounding is the identity and the exact parts sum to the
 * exact total.
 *
 * `parts` and `total` are the exact strings from the server; the answer is only a number of
 * places, so the caller still renders every cell through `quantity` - one rounding function.
 * If the inputs do not foot even exactly (a server defect), this returns the places at which they
 * are shown in full, so the disagreement is visible rather than rounded away.
 */
export function placesThatFoot(parts: readonly string[], total: string, base: number): number {
  const widths = [...parts, total].map(significantPlaces);
  if (widths.some((width) => width === undefined)) return base;
  const exact = Math.max(base, ...(widths as number[]));
  for (let places = base; places < exact; places += 1) {
    const printed = parts.map((part) => roundDecimal(part, places));
    const printedTotal = roundDecimal(total, places);
    if (printedTotal === undefined || printed.some((part) => part === undefined)) return base;
    const sum = sumDecimals(printed as string[]);
    if (sum !== undefined && sameAmount(sum, printedTotal)) return places;
  }
  return exact;
}
